import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Fade,
  Alert,
  InputAdornment,
  Link,
  Paper,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import Navbar from '../components/Navbar.js';
import Footer from '../components/Footer.js';
import { ENDPOINTS, apiCall } from '../utils/api.js';

const SUPPORT_EMAIL = 'support@sqipqueue.com';

const EMPTY_FORM = { name: '', email: '', message: '' };
const EMPTY_ERRORS = { name: '', email: '', message: '' };

function validate(fields) {
  const errs = { ...EMPTY_ERRORS };
  if (!fields.name.trim()) errs.name = 'Name is required.';
  if (!fields.email.trim()) {
    errs.email = 'Email is required.';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fields.email.trim())) {
    errs.email = 'Please enter a valid email address.';
  }
  if (!fields.message.trim()) {
    errs.message = 'Message is required.';
  } else if (fields.message.trim().length < 10) {
    errs.message = 'Please provide a little more detail.';
  }
  return errs;
}

const hasErrors = (errs) => Object.values(errs).some(Boolean);

// ── Small "how to reach us" info row ──────────────────────────────────────────
function InfoItem({ icon, label, children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
      <Box sx={{ color: 'primary.main', mt: '2px' }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {children}
        </Typography>
      </Box>
    </Box>
  );
}

export default function ContactUsPage() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [touched, setTouched] = useState({});
  const [submitStatus, setSubmitStatus] = useState(null); // null | 'loading' | 'success' | 'error'
  const [submitError, setSubmitError] = useState('');

  const handleChange = (field) => (e) => {
    const val = e.target.value;
    setForm((prev) => ({ ...prev, [field]: val }));
    if (touched[field]) {
      setErrors((prev) => ({ ...prev, [field]: validate({ ...form, [field]: val })[field] }));
    }
  };

  const handleBlur = (field) => () => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    setErrors((prev) => ({ ...prev, [field]: validate(form)[field] }));
  };

  const handleSubmit = async () => {
    setTouched({ name: true, email: true, message: true });
    const errs = validate(form);
    setErrors(errs);
    if (hasErrors(errs)) return;

    setSubmitStatus('loading');
    setSubmitError('');
    try {
      await apiCall(ENDPOINTS.CONTACT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          message: form.message.trim(),
        }),
      });
      setSubmitStatus('success');
    } catch (err) {
      setSubmitError(err.message || 'Something went wrong. Please try again.');
      setSubmitStatus('error');
    }
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: 'background.paper' },
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (submitStatus === 'success') {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
        <Navbar />
        <Fade in timeout={400}>
          <Box
            sx={{
              flex: 1,
              maxWidth: 520,
              mx: 'auto',
              px: { xs: 2, sm: 3 },
              py: { xs: 8, sm: 12 },
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <CheckCircleOutlineIcon sx={{ fontSize: 64, color: 'success.main', mb: 1 }} />
            <Typography variant="h5" fontWeight={800}>
              Message received!
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
              Thanks for reaching out. We'll reply to <strong>{form.email.trim()}</strong> within one
              business day.
            </Typography>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/')}
              sx={{ mt: 1, borderRadius: 2.5, fontWeight: 600 }}
            >
              Back to home
            </Button>
          </Box>
        </Fade>
        <Footer />
      </Box>
    );
  }

  // ── Main page ─────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>
      <Navbar />

      <Box
        sx={{
          flex: 1,
          maxWidth: 720,
          mx: 'auto',
          width: '100%',
          px: { xs: 2, sm: 3 },
          py: { xs: 5, sm: 8 },
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
        }}
      >
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <Fade in timeout={400}>
          <Box>
            <Typography variant="h4" fontWeight={900} sx={{ letterSpacing: '-0.02em', mb: 1.5, lineHeight: 1.2 }}>
              Contact us
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.75, maxWidth: 560 }}>
              Questions, feedback, or need a hand with your queue or appointment? Send us a message
              below and we'll get back to you — or email us directly any time.
            </Typography>
          </Box>
        </Fade>

        {/* ── Direct contact info ─────────────────────────────────────────── */}
        <Fade in timeout={450}>
          <Paper
            variant="outlined"
            sx={{
              borderRadius: 4,
              px: { xs: 2.5, sm: 3.5 },
              py: { xs: 2.5, sm: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              flexWrap: 'wrap',
              gap: { xs: 2, sm: 4 },
            }}
          >
            <InfoItem icon={<EmailOutlinedIcon fontSize="small" />} label="Email us">
              <Link href={`mailto:${SUPPORT_EMAIL}`} underline="hover" sx={{ fontWeight: 600 }}>
                {SUPPORT_EMAIL}
              </Link>
            </InfoItem>
            <InfoItem icon={<ScheduleOutlinedIcon fontSize="small" />} label="Response time">
              Within one business day
            </InfoItem>
            <InfoItem icon={<LocationOnOutlinedIcon fontSize="small" />} label="Location">
              Toronto, Ontario, Canada
            </InfoItem>
          </Paper>
        </Fade>

        {/* ── Form card ───────────────────────────────────────────────────── */}
        <Fade in timeout={500}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 4,
              px: { xs: 2.5, sm: 4 },
              py: { xs: 3, sm: 4 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2.5,
            }}
          >
            <TextField
              fullWidth
              label="Your name"
              value={form.name}
              onChange={handleChange('name')}
              onBlur={handleBlur('name')}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
            <TextField
              fullWidth
              label="Email address"
              type="email"
              value={form.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              error={Boolean(touched.email && errors.email)}
              helperText={(touched.email && errors.email) || 'We\'ll reply to this address.'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailOutlinedIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={inputSx}
            />
            <TextField
              fullWidth
              label="How can we help?"
              value={form.message}
              onChange={handleChange('message')}
              onBlur={handleBlur('message')}
              error={Boolean(touched.message && errors.message)}
              helperText={touched.message && errors.message}
              multiline
              minRows={4}
              inputProps={{ maxLength: 4000 }}
              sx={inputSx}
            />

            {submitStatus === 'error' && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {submitError}
              </Alert>
            )}

            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={
                submitStatus === 'loading' ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <SendOutlinedIcon />
                )
              }
              onClick={handleSubmit}
              disabled={submitStatus === 'loading'}
              sx={{
                borderRadius: 2.5,
                fontWeight: 700,
                color: '#fff',
                py: 1.5,
                alignSelf: { xs: 'stretch', sm: 'flex-end' },
                minWidth: 180,
              }}
            >
              {submitStatus === 'loading' ? 'Sending…' : 'Send message'}
            </Button>
          </Box>
        </Fade>
      </Box>

      <Footer />
    </Box>
  );
}
